<template>
    <div class="nav-bar-wrapper">
        <div class="nav-new-left">
            <div class="nav-new-icons" :class="{ collapsed: !showLabels }">
                <div
                    v-for="option in items"
                    :key="option.name"
                    class="nav-new-icon-btn"
                    :class="{ active: isActive(option), disabled: option.disabled }"
                    @click="handleClick($event, option)"
                    @mouseenter="startPeek(option.name)"
                    @mouseleave="endPeek"
                >
                    <Icon
                        :name="option.icon?.name"
                        class="nav-toggle"
                        :class="option.icon?.name"
                        :size="'lg'"
                    />
                    <span v-show="showLabels" class="nav-label">{{ option.label }}</span>
                </div>

                <div class="nav-new-icons__show-hide labels">
                    <Icon
                        name="arrow-right-to-line"
                        class="nav-toggle"
                        :class="{ 'labels-visible': showLabels, rotated: !showLabels }"
                        :size="'lg'"
                        :color="'var(--text-color-subtitle)'"
                        @click="$emit('toggle-labels')"
                    />
                </div>
            </div>
        </div>

        <div
            class="vms-nav-drawer-region"
            :class="{ 'open': leftPanelOpened || !!hoveredNavButton }"
            @mouseenter="keepPeek"
            @mouseleave="endPeek"
        >
            <nav-drawer
                :nav-item="drawerNavItem"
                :show-grid-controls="showGridControls"
                @close="closeDrawer"
                @commit="onCommit"
            >
                <!-- TODO: Make adminItems generic when connected to navigationStore -->
                <template v-if="drawerNavItem?.name === 'ADMIN_OPT'" #drawer-content>
                    <div class="drawer-admin-list">
                        <button
                            v-for="item in adminItems"
                            :key="item.value"
                            class="drawer-admin-item"
                            @click="$emit('action', { item })"
                        >{{ item.text }}</button>
                    </div>
                </template>
            </nav-drawer>
        </div>
    </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import Icon from '@common/icon/Icon.vue';

export default {
    name: 'NavBar',
    components: {
        Icon,
        'nav-drawer': defineAsyncComponent(() => import('./NavDrawer.vue')),
    },
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        activeItem: {
            type: String,
            default: null,
        },
        showLabels: {
            type: Boolean,
            default: true,
        },
        adminItems: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['navigate', 'toggle-labels', 'commit', 'action'],
    data() {
        return {
            leftPanelOpened: false,
            hoveredNavButton: null,
        };
    },
    computed: {
        drawerNavItem() {
            const button = this.hoveredNavButton || this.activeItem;
            if (!button) return null;
            return this.items.find((item) => item.name === button) || null;
        },
        showGridControls() {
            if (!this.drawerNavItem?.isGrid) return false;
            if (this.hoveredNavButton && this.hoveredNavButton !== this.activeItem) return false;
            return true;
        },
    },
    methods: {
        isActive(option) {
            return option.name === this.activeItem;
        },
        handleClick(event, option) {
            if (option.disabled) return;
            if (this.activeItem === option.name && this.leftPanelOpened) {
                this.leftPanelOpened = false;
                this.hoveredNavButton = null;
                clearTimeout(this._peekTimer);
                return;
            }
            this.leftPanelOpened = true;
            this.$emit('navigate', option);
        },
        startPeek(name) {
            clearTimeout(this._peekTimer);
            this.hoveredNavButton = name;
        },
        keepPeek() {
            clearTimeout(this._peekTimer);
        },
        endPeek() {
            this._peekTimer = setTimeout(() => {
                this.hoveredNavButton = null;
            }, 80);
        },
        closeDrawer() {
            this.leftPanelOpened = false;
            this.hoveredNavButton = null;
            clearTimeout(this._peekTimer);
        },
        onCommit(navItem) {
            this.leftPanelOpened = true;
            this.$emit('commit', navItem);
        },
    },
    beforeUnmount() {
        clearTimeout(this._peekTimer);
    },
};
</script>

<style scoped>
.nav-bar-wrapper {
    display: contents;
}

.nav-new-left {
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: var(--surface-default);
    transition: all .05s ease-in-out;
}

.nav-new-left .nav-new-icons.collapsed .nav-label {
    opacity: 0;
    transform: translateX(-10px);
    pointer-events: none;
}

.nav-new-icon-btn {
    cursor: default;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-6);
}

.nav-new-icon-btn:hover {
    background-color: var(--surface-subtle);
    color: var(--text-color-body);
}

.nav-new-icon-btn:focus-visible {
    outline: 2px solid var(--border-color-default-primary);
    outline-offset: -2px;
}

.nav-new-icon-btn.active {
    background-color: var(--surface-default-primary);
    color: var(--text-color-inverse);
    font-weight: var(--text-weight-medium);
}

.nav-new-icon-btn.active i {
    color: var(--text-color-inverse) !important;
}

.nav-new-icon-btn.disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
}

.nav-new-icon-btn:hover .nav-new-tooltip {
    opacity: 1;
    transform: translateX(0);
}

.nav-new-icons {
    display: flex;
    flex-direction: column;
    align-items: baseline;
    flex-grow: 1;
    width: 100%;
    padding-top: var(--space-2);
    padding-bottom: var(--space-4);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-new-icons.collapsed {
    display: flex;
    flex-direction: column;
    align-items: baseline;
    transition: transform var(--transition-3) cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-new-icons__show-hide {
    display: flex;
    padding: 0 var(--space-8);
    margin-top: auto;
    height: fit-content;
    width: 100%;
    justify-content: flex-start;
}

.nav-new-left i.nav-toggle {
    transition: transform 0.3s ease;
}

.nav-new-left i.nav-toggle.rotated {
    transform: rotate(-180deg);
}

.nav-new-icons > div {
    width: 100%;
}

.nav-new-icon-btn span.nav-label {
    font-size: var(--text-size-base);
    padding: 0 var(--space-6);
    white-space: nowrap;
    width: 100px;
    display: inline-block;
    opacity: 1;
    transform: translateX(0);
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.vms-nav-drawer-region {
    max-width: 0;
    overflow: hidden;
    transition: max-width 0.25s ease;
    background-color: var(--surface-default);
    border-left: 1px solid var(--border-color-default);
    box-shadow: 2px 0 8px rgba(15, 23, 42, 0.06);
}

.vms-nav-drawer-region.open {
    max-width: 350px;
}

.drawer-admin-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
}

.drawer-admin-item {
    display: flex;
    align-items: center;
    width: 100%;
    padding: var(--space-4) var(--space-5);
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    font-size: var(--text-size-base);
    color: var(--text-color-title);
    border-radius: var(--radius-xs);
    transition: background-color 0.15s ease;
}

.drawer-admin-item:hover {
    background-color: var(--surface-subtle);
}

.drawer-admin-item:focus-visible {
    outline: 2px solid var(--border-color-default-primary);
    outline-offset: -2px;
}
</style>
